/**
 * ManagedVideo — always-muted, looping scene/background video element.
 *
 * Audio policy: videos NEVER play audio. All app audio comes from the
 * soundtrack engine; video loops are silent. The element is hard-muted
 * (imperatively at mount via the ref AND via the `muted` attribute) and
 * consumers cannot opt in to audio. Note that muting does NOT stop the browser
 * from DECODING an embedded audio track — a video shipped with a malformed
 * audio track can still throw MEDIA_ERR_DECODE — so source videos are also
 * audio-stripped at the asset layer (`scripts/strip-video-audio.sh`).
 *
 * Supports controlled pause/resume via the `paused` prop (parents silence the
 * inline video while an enlarge modal is showing), and recovers from the
 * "mixed cache state" failure after a server-side asset swap (e.g. audio
 * strip): a stale cached copy can either (a) leave a Range request landing past
 * the new EOF → HTTP 416, stuck at readyState 0 with NO error event, or
 * (b) MEDIA_ERR_DECODE on a now-stale track. Both are recovered by retrying
 * with a cache-buster query param so the browser ignores the poisoned entry.
 *
 * Defaults: autoPlay, loop, playsInline (overridable via props except muted).
 */

import { forwardRef, useRef, useEffect, useCallback } from 'react';

type VideoHTMLProps = Omit<React.ComponentPropsWithoutRef<'video'>, 'muted' | 'onError'>;

interface ManagedVideoProps extends VideoHTMLProps {
  /**
   * When true, the video is paused. When false (default), it plays subject to
   * autoplay policy. Safe to toggle at runtime.
   */
  paused?: boolean;
  /** Called when autoplay is blocked by browser policy. */
  onAutoplayBlocked?: () => void;
  /**
   * Called once recovery is exhausted: a media error after MAX_RETRIES, or the
   * stuck-load watchdog giving up. The watchdog path has no DOM event, so the
   * argument is optional.
   */
  onError?: (e?: React.SyntheticEvent<HTMLVideoElement>) => void;
}

const MAX_RETRIES = 2;
const STUCK_TIMEOUT_MS = 5000;

const ManagedVideo = forwardRef<HTMLVideoElement, ManagedVideoProps>(
  (
    { onCanPlay, onError, onAutoplayBlocked, paused = false, src, children, ...props },
    externalRef
  ) => {
    const internalRef = useRef<HTMLVideoElement>(null);
    const pausedRef = useRef(paused);
    pausedRef.current = paused;
    // Latest onError, read by the watchdog through a ref so armStuckWatchdog
    // stays identity-stable: with onError itself in its deps, a consumer
    // passing an inline handler would re-run the arm effect on every parent
    // render, resetting the retry budget and pushing the 5s deadline out
    // forever (ScenePanel re-renders far more often than every 5s).
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    // Monotonic counter bumped per play() attempt. The catch handler bails if it
    // has moved on — guards a stale play() AbortError (from a pause→resume race)
    // firing a spurious onAutoplayBlocked after the video is already playing.
    const playAttemptRef = useRef(0);

    // Deferred media release, armed on ref detach. pause() alone keeps the
    // demuxer, decoder, and frame buffers alive on a detached element ,
    // native memory the JS GC never sees pressure from, so every remount
    // site (scene swaps, chat bubbles, background crossfades) would leak a
    // decoder per remount until the renderer OOMs (player-reported SIGILL
    // crash loop, 2026-07-13). The release is deferred one tick and canceled
    // on re-attach because React also nulls callback refs while the element
    // stays live (StrictMode's dev remount, ref-identity changes), an
    // immediate release there would blank a playing video, and React never
    // re-stamps an unchanged `src` prop on the surviving host element.
    const pendingReleaseRef = useRef<{ el: HTMLVideoElement; timer: number } | null>(null);

    // Merge external + internal refs. Pause on ref cleanup (null) because React
    // clears callback refs BEFORE running effect cleanups, so a useEffect-based
    // pause would be a no-op. Hard-mute synchronously at mount, before the
    // browser's autoplay decision, so unmuted autoplay can't leak through.
    const setRef = useCallback(
      (el: HTMLVideoElement | null) => {
        if (!el && internalRef.current) {
          const dying = internalRef.current;
          dying.pause();
          const timer = window.setTimeout(() => {
            pendingReleaseRef.current = null;
            dying.removeAttribute('src');
            dying.load();
          }, 0);
          pendingReleaseRef.current = { el: dying, timer };
        }
        if (el) {
          if (pendingReleaseRef.current?.el === el) {
            window.clearTimeout(pendingReleaseRef.current.timer);
            pendingReleaseRef.current = null;
          }
          el.muted = true;
        }
        internalRef.current = el;
        if (typeof externalRef === 'function') externalRef(el);
        else if (externalRef)
          (externalRef as React.RefObject<HTMLVideoElement | null>).current = el;
      },
      [externalRef]
    );

    const tryPlay = useCallback(() => {
      const video = internalRef.current;
      if (!video || pausedRef.current) return;
      // Never (re)start decoding in a hidden tab: a `paused` flip or a
      // source landing while hidden would undo the visibilitychange pause.
      // The visibility handler resumes on return.
      if (document.hidden) return;
      video.muted = true;
      const attempt = ++playAttemptRef.current;
      video.play().catch((err) => {
        if (attempt !== playAttemptRef.current) return;
        console.warn('Video play failed:', err);
        onAutoplayBlocked?.();
      });
    }, [onAutoplayBlocked]);

    // Controlled pause/resume. Pause is synchronous; resume only attempts when
    // the video is loaded enough — otherwise handleCanPlay plays on data arrival.
    useEffect(() => {
      const video = internalRef.current;
      if (!video) return;
      if (paused) video.pause();
      else if (video.readyState >= 2) tryPlay();
    }, [paused, tryPlay]);

    // ── Poisoned-cache recovery, shared by the stuck-load watchdog and the
    // error handler. Retries the SAME src with a cache-buster so the browser
    // re-fetches past any stale cached entry. Bounded by MAX_RETRIES per src.
    const retryCountRef = useRef(0);
    const stuckTimerRef = useRef<number | null>(null);

    const clearStuckTimer = useCallback(() => {
      if (stuckTimerRef.current !== null) {
        window.clearTimeout(stuckTimerRef.current);
        stuckTimerRef.current = null;
      }
    }, []);

    const retryWithCacheBust = useCallback((): boolean => {
      const v = internalRef.current;
      if (!v || !src || retryCountRef.current >= MAX_RETRIES) return false;
      retryCountRef.current += 1;
      const sep = src.includes('?') ? '&' : '?';
      v.src = `${src}${sep}_r=${retryCountRef.current}_${Date.now()}`;
      v.load();
      return true;
    }, [src]);

    // Stuck-load watchdog: if `canplay` hasn't fired within STUCK_TIMEOUT_MS and
    // the element is still loading with no data (the 416-past-EOF case, which
    // fires NO error event), retry with a cache-buster. After exhausting
    // retries, forward to the consumer's onError so it can fall back to a
    // placeholder instead of an invisible video (symmetric with handleError).
    const armStuckWatchdog = useCallback(() => {
      clearStuckTimer();
      stuckTimerRef.current = window.setTimeout(() => {
        stuckTimerRef.current = null;
        const v = internalRef.current;
        if (!v || !src) return;
        const stuck = v.readyState < 2 && v.networkState === 2; // HAVE_NOTHING/METADATA + LOADING
        if (!stuck) return;
        if (retryWithCacheBust()) armStuckWatchdog();
        else onErrorRef.current?.();
      }, STUCK_TIMEOUT_MS);
    }, [src, clearStuckTimer, retryWithCacheBust]);

    useEffect(() => {
      retryCountRef.current = 0;
      armStuckWatchdog();
      return clearStuckTimer;
    }, [src, armStuckWatchdog, clearStuckTimer]);

    // Force a clean reload when the consumer swaps `src` on the SAME <video>
    // element. Setting the attribute alone can carry the PREVIOUS clip's byte
    // offset into a Range request past the new (often smaller) file's EOF → 416
    // → the new source never reaches `canplay`. `load()` restarts from byte 0.
    const loadedSrcRef = useRef(src);
    useEffect(() => {
      const video = internalRef.current;
      if (!video || loadedSrcRef.current === src) return;
      loadedSrcRef.current = src;
      video.load();
    }, [src]);

    // Timer cleanup on unmount (the media release itself is armed by setRef's
    // detach branch, refs are already null when effect cleanups run).
    useEffect(() => {
      return () => {
        clearStuckTimer();
      };
    }, [clearStuckTimer]);

    // Stop decoding while the tab is hidden, muted looping videos keep the
    // decoder hot in background tabs. Resume respects the `paused` prop.
    useEffect(() => {
      const onVisibility = () => {
        const video = internalRef.current;
        if (!video) return;
        if (document.hidden) video.pause();
        else if (!pausedRef.current && video.readyState >= 2) tryPlay();
      };
      document.addEventListener('visibilitychange', onVisibility);
      return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [tryPlay]);

    const handleCanPlay = useCallback(
      (e: React.SyntheticEvent<HTMLVideoElement>) => {
        clearStuckTimer();
        tryPlay();
        onCanPlay?.(e);
      },
      [onCanPlay, tryPlay, clearStuckTimer]
    );

    // Media errors on a poisoned cache entry (MEDIA_ERR_DECODE on a stale track,
    // MEDIA_ERR_NETWORK, MEDIA_ERR_SRC_NOT_SUPPORTED) are recovered by a
    // cache-busting reload — the same fix as the stuck watchdog, but for the
    // case where the cached bytes load yet fail to decode. MEDIA_ERR_ABORTED (1)
    // is benign (fires on src swap / load()). After exhausting retries, forward
    // to the consumer's onError so it can fall back to a placeholder.
    const handleError = useCallback(
      (e: React.SyntheticEvent<HTMLVideoElement>) => {
        const code = internalRef.current?.error?.code;
        if (code === 1) return; // MEDIA_ERR_ABORTED — benign
        clearStuckTimer();
        if (retryWithCacheBust()) {
          armStuckWatchdog();
          return;
        }
        onError?.(e);
      },
      [onError, retryWithCacheBust, armStuckWatchdog, clearStuckTimer]
    );

    return (
      <video
        ref={setRef}
        src={src}
        muted
        autoPlay
        loop
        playsInline
        onCanPlay={handleCanPlay}
        onError={handleError}
        {...props}
      >
        {children}
      </video>
    );
  }
);

ManagedVideo.displayName = 'ManagedVideo';

export default ManagedVideo;
